import React, { useEffect, useState } from "react";
import { Loader2, MessageSquare } from "lucide-react";
import api from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";

export default function Comments({ postId }) {
  const { toast } = useToast();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/posts/${postId}/comments`).then((r) => setComments(r.data)).finally(() => setLoading(false));
  }, [postId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !body.trim()) {
      toast({ title: "Name and comment are required", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/posts/${postId}/comments`, { name, body, website });
      setName(""); setBody("");
      toast({ title: "Thanks \u2014 your comment is awaiting approval", description: "The Curmudgeon reviews comments before they appear." });
    } catch (err) {
      toast({ title: "Could not submit comment", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="max-w-3xl mx-auto px-6 pb-20">
      <div className="border-t border-[#2a2a2a] pt-10">
        <h3 className="text-2xl font-bold uppercase text-[#e63946] mb-6 flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
          <MessageSquare className="w-5 h-5" /> Comments{comments.length > 0 ? ` (${comments.length})` : ""}
        </h3>

        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin text-[#e63946]" />
        ) : comments.length === 0 ? (
          <p className="text-gray-500 italic mb-8">No comments yet. Poke the bear below.</p>
        ) : (
          <div className="space-y-5 mb-10">
            {comments.map((c) => (
              <div key={c.id} className="bg-[#1c1c1c] border border-[#2a2a2a] rounded-lg p-4">
                <p className="font-semibold text-gray-100" style={{ fontFamily: 'Oswald, sans-serif' }}>{c.name}</p>
                <p className="text-gray-300 mt-1 whitespace-pre-wrap">{c.body}</p>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={submit} className="bg-[#161618] border border-[#26262a] rounded-xl p-5 space-y-4">
          <p className="text-gray-200 font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Leave a comment</p>
          <div>
            <Label htmlFor="c-name" className="text-gray-400">Name</Label>
            <Input id="c-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-[#0f0f10] border-[#2a2a2e] text-white" />
          </div>
          <div>
            <Label htmlFor="c-body" className="text-gray-400">Comment</Label>
            <Textarea id="c-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="mt-1.5 bg-[#0f0f10] border-[#2a2a2e] text-white" />
          </div>
          <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
          <Button type="submit" disabled={submitting} className="bg-[#e63946] hover:bg-[#d12f3c] text-white">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post comment"}
          </Button>
          <p className="text-xs text-gray-500">Comments are reviewed before they appear.</p>
        </form>
      </div>
    </section>
  );
}
